/**
 * Centralized route paths and URL builders.
 *
 * Single source of truth for every in-app route. Replaces scattered hardcoded
 * strings (`navigate('/contacts')`, `<Link to="/dashboard">`, etc.) that were
 * previously duplicated across ~30 production files plus their tests.
 *
 * @example
 *   import { paths } from '@/constants';
 *   navigate(paths.dashboard);
 *   navigate(paths.contact(contactId));
 *   navigate(paths.packetEdit(packetId));
 */

export const paths = {
  // Root + dashboard
  root: '/',
  dashboard: '/dashboard',
  dashboardCertificates: '/dashboard/certificates',
  dashboardLibrary: '/dashboard/library',

  // Public auth flow
  login: '/login',
  loginSignIn: '/login/signIn',
  loginSignUp: '/login/signUp',
  loginVerify: '/login/verify',
  resetPassword: '/reset-password',
  logout: '/logout',
  sso: '/sso',

  // Consume (public viewer)
  // `link` is the optional recipient-link path segment (vingapi#1123); no
  // caller passes it yet, so every in-app link stays `/view/<stub>`.
  view: (stub: string, link?: string) => (link ? `/view/${stub}/${link}` : `/view/${stub}`),
  // Where a viewer-level user lands after sign-in or completing content.
  // Audit C4: this used to be '/user/assignments' — a legacy-Angular route
  // with NO entry in this app's route table, so every viewer landed on the
  // 404 catch-all. The React viewer home is the dashboard.
  viewerHome: '/dashboard',

  // Settings + admin
  manage: '/manage',
  admin: '/admin',
  adminTab: (tool: string) => `/admin/${tool}`,
  reportLatest: (key: string) => `/reports/${key}/latest`,

  // Domain entity lists / details. ID params accept string | number so callers
  // don't have to .toString() server-issued numeric ids at every site.
  contacts: '/contacts',
  contact: (contactId: string | number) => `/contacts/${contactId}`,
  contactTab: (contactId: string | number, tab: string) =>
    `/contacts/${contactId}/${tab}`,

  packets: '/packets',
  packet: (packetId: string | number) => `/packets/${packetId}`,
  packetEdit: (packetId: string | number) => `/packets/${packetId}/edit`,

  checklists: '/checklists',
  checklist: (checklistId: string | number) => `/checklists/${checklistId}`,
  checklistEdit: (checklistId: string | number) => `/checklists/${checklistId}/edit`,

  analyticsTab: (tab: string) => `/analytics/${tab}`,
} as const;
